import React, { useMemo } from 'react';
import type { ICellRendererParams } from 'ag-grid-community';
import type { ReviewCellRendererProps, OwnershipItem } from '../helpers';
import { replaceArrayItem } from 'venn-utils';
import { MultiOwnershipContextSelector } from '../MultiOwnershipContextSelector';

export const OwnershipCellRenderer = (props: ICellRendererParams & ReviewCellRendererProps) => {
  const {
    data,
    currentMapping,
    additionalMetadata,
    onMappingChange,
    ownershipSelectorState,
    setOwnershipSelectorState,
    existingOwnerships,
    availableOwnerships,
    updateMappingOptimistically,
  } = props;
  const { seriesId } = data;
  const { isNew } = additionalMetadata.perSeries[seriesId]!;

  const assignedContexts: string[] = useMemo(() => data.ownerContextIds ?? [], [data.ownerContextIds]);

  const ownershipItems: OwnershipItem[] = useMemo(() => {
    const existedIds = existingOwnerships[seriesId];

    return availableOwnerships.map((item) => ({
      value: item.value,
      label: item.label,
      checked: !!assignedContexts?.includes(item.value),
      disabled: !isNew && !!assignedContexts?.includes(item.value) && !!existedIds?.includes(item.value),
    }));
  }, [assignedContexts, seriesId, existingOwnerships, isNew, availableOwnerships]);

  const onToggle = (updatedItem: OwnershipItem) => {
    const prevOwnerContextIds: string[] = assignedContexts ?? [];
    const updatedOwnerContextIds = updatedItem.checked
      ? [...prevOwnerContextIds, updatedItem.value]
      : prevOwnerContextIds.filter((id) => id !== updatedItem.value);

    const columnToUpdateIndex = currentMapping.columns.findIndex((c) => c.seriesId === seriesId);
    const updatedColumn = {
      ...data,
      ownerContextIds: updatedOwnerContextIds,
    };
    const newMapping = {
      ...currentMapping,
      columns: replaceArrayItem(currentMapping.columns, columnToUpdateIndex, updatedColumn),
    };
    updateMappingOptimistically(newMapping);
  };

  const showOwnershipSelector = () => {
    if (ownershipSelectorState.open && ownershipSelectorState.rowIdForOwnershipSelector !== seriesId) {
      onMappingChange(currentMapping);
    }
    setOwnershipSelectorState((prev) => ({
      ...prev,
      rowIdForOwnershipSelector: seriesId,
      open: true,
    }));
  };

  const hideOwnershipSelector = () => {
    setOwnershipSelectorState((prev) => ({
      ...prev,
      rowIdForOwnershipSelector: '',
      open: false,
    }));
    onMappingChange(currentMapping);
  };

  return (
    <MultiOwnershipContextSelector
      assignedContexts={assignedContexts}
      shouldShowOwnershipSelector={
        ownershipSelectorState.open && ownershipSelectorState.rowIdForOwnershipSelector === seriesId
      }
      showOwnershipSelector={showOwnershipSelector}
      hideOwnershipSelector={hideOwnershipSelector}
      ownershipItems={ownershipItems}
      onToggle={onToggle}
    />
  );
};
